import { Box, Typography } from "@mui/material";
import useColors from "api/hooks/useColors";
import TextButton from "components/buttons/TextButton";
import CustomTooltip from "components/CustomTooltip";
import FilterMenu from "components/filters/FilterMenu";
import SearchAndOrFilter from "components/filters/SearchAndOrFilter";
import SearchInput from "components/filters/SearchInput";
import StyledDataGrid from "components/StyledDataGrid";
import { useCallback, useMemo, useState } from "react";
import IntegrationImage from "scenes/stack/integrations/components/IntegrationImage";

const CustomersTableWidget = ({ data, controls = [], isLoading }) => {
  const colors = useColors();
  const [appliedFilters, setAppliedFilters] = useState({
    envFilter: {},
    customerFilter: {},
    toolFilter: {},
    searchString: "",
  });
  const [filterAndOrLogics, setFilterAndOrLogics] = useState({
    toolFilter: "OR",
  });

  const handleClearFilters = () => {
    setAppliedFilters({
      envFilter: {},
      customerFilter: {},
      toolFilter: {},
      searchString: "",
    });
    setFilterAndOrLogics({ toolFilter: "OR" });
  };

  const envOptions =
    data?.env_ids?.map((env) => ({
      name: env,
      label: env,
      displayName: env,
    })) || [];

  const customerOptions = data?.customers
    ? [...new Set(data.customers.map((c) => c.customer_id))].map(
        (customer) => ({
          name: customer,
          label: customer,
          displayName: customer,
        })
      )
    : [];

  const toolOptions =
    data?.integrations?.map((integration) => ({
      name: integration.integration,
      label: integration.integration,
      displayName: integration.integration,
    })) || [];

  const controlIdToTool = useMemo(() => {
    const mapping = {};
    controls.forEach((control) => {
      if (control.control_id && control.tool) {
        mapping[control.control_id] = control.tool;
      }
    });
    return mapping;
  }, [controls]);

  const selectedTools = Object.keys(appliedFilters.toolFilter || {}).filter(
    (key) => appliedFilters.toolFilter[key]
  );

  const filterControlIdsByTool = useCallback(
    (controlIds) => {
      if (selectedTools.length === 0) return controlIds;
      return controlIds.filter((id) => {
        const tool = controlIdToTool[id];
        return tool && selectedTools.includes(tool);
      });
    },
    [controlIdToTool, selectedTools]
  );

  const getControlsCountByCustomerAndTool = useCallback(
    (customerId, envId) => {
      if (selectedTools.length === 0) return null;
      return controls.filter((control) => {
        const toolMatches =
          control.tool && selectedTools.includes(control.tool);
        const belongsToCustomer = (control.customers || []).some(
          (c) => c.customer_id === customerId && c.env_id === envId
        );
        return toolMatches && belongsToCustomer;
      }).length;
    },
    [controls, selectedTools]
  );

  const filteredCustomers = useMemo(() => {
    if (!data?.customers) return [];
    let result = [...data.customers];

    const selectedEnvs = Object.keys(appliedFilters.envFilter || {}).filter(
      (key) => appliedFilters.envFilter[key]
    );
    if (selectedEnvs.length > 0) {
      result = result.filter((c) => selectedEnvs.includes(c.env_id));
    }

    const selectedCustomerIds = Object.keys(
      appliedFilters.customerFilter || {}
    ).filter((key) => appliedFilters.customerFilter[key]);
    if (selectedCustomerIds.length > 0) {
      result = result.filter((c) =>
        selectedCustomerIds.includes(c.customer_id)
      );
    }

    if (selectedTools.length > 0) {
      const logic = filterAndOrLogics.toolFilter;
      if (logic === "AND") {
        result = result.filter((c) =>
          selectedTools.every((tool) => c.integrations.includes(tool))
        );
      } else {
        result = result.filter((c) =>
          c.integrations.some((tool) => selectedTools.includes(tool))
        );
      }
    }

    if (appliedFilters.searchString) {
      const search = appliedFilters.searchString.toLowerCase();
      result = result.filter(
        (c) =>
          c.customer_id.toLowerCase().includes(search) ||
          c.env_id.toLowerCase().includes(search)
      );
    }

    if (selectedTools.length > 0) {
      result = result.map((customer) => {
        const filteredControlsCount = getControlsCountByCustomerAndTool(
          customer.customer_id,
          customer.env_id
        );
        return {
          ...customer,
          controls_gap_ids: filterControlIdsByTool(customer.controls_gap_ids),
          controls_snoozed_ids: filterControlIdsByTool(
            customer.controls_snoozed_ids
          ),
          controls_excluded_ids: filterControlIdsByTool(
            customer.controls_excluded_ids
          ),
          controls_count:
            filteredControlsCount !== null
              ? filteredControlsCount
              : customer.controls_count,
        };
      });
    }

    return result;
  }, [
    data?.customers,
    appliedFilters,
    filterAndOrLogics.toolFilter,
    selectedTools,
    filterControlIdsByTool,
    getControlsCountByCustomerAndTool,
  ]);

  const customerColumns = [
    {
      field: "customer_id",
      headerName: "Customer ID",
      flex: 1,
      renderCell: (params) => (
        <Typography variant="body2" sx={{ fontSize: "13px" }}>
          {params.value}
        </Typography>
      ),
    },
    {
      field: "env_id",
      headerName: "Platform Env",
      flex: 1,
      renderCell: (params) => (
        <Typography variant="body2" sx={{ fontSize: "13px" }}>
          {params.value}
        </Typography>
      ),
    },
    {
      field: "integrations",
      headerName: "Integrations",
      flex: 2,
      renderCell: (params) => {
        const tools = params.value || [];
        const displayTools = tools.slice(0, 8);
        const remaining = tools.length - 8;

        return (
          <Box display="flex" gap={0.75} flexWrap="wrap" alignItems="center">
            {displayTools.map((tool, idx) => {
              const toolName = tool?.toLowerCase().replace(/ /g, "_");
              return (
                <Box key={idx}>
                  <IntegrationImage
                    name={toolName}
                    width="24px"
                    showToolTip={true}
                    newLogo={true}
                  />
                </Box>
              );
            })}
            {remaining > 0 && (
              <CustomTooltip title={tools.slice(8).join(", ")}>
                <Typography variant="caption" color="text.secondary">
                  +{remaining} more
                </Typography>
              </CustomTooltip>
            )}
          </Box>
        );
      },
    },
    {
      field: "controls_count",
      headerName: "Controls",
      flex: 0.5,
      align: "left",
      headerAlign: "left",
      renderCell: (params) => (
        <Typography variant="body2" sx={{ fontSize: "13px" }}>
          {params.value}
        </Typography>
      ),
    },
    {
      field: "controls_gap_ids",
      headerName: "Gaps",
      flex: 0.6,
      align: "left",
      headerAlign: "left",
      valueGetter: (params) => (params.row?.controls_gap_ids || []).length,
      renderCell: (params) => {
        const gapIds = params.row.controls_gap_ids || [];
        if (gapIds.length === 0)
          return (
            <Typography variant="body2" sx={{ fontSize: "13px" }}>
              0
            </Typography>
          );
        return (
          <CustomTooltip title={gapIds.join(", ")}>
            <Typography
              variant="body2"
              sx={{ cursor: "pointer", color: colors.error, fontSize: "13px" }}
            >
              {gapIds.length}
            </Typography>
          </CustomTooltip>
        );
      },
    },
    {
      field: "controls_snoozed_ids",
      headerName: "Snoozed",
      flex: 0.6,
      align: "left",
      headerAlign: "left",
      valueGetter: (params) => (params.row?.controls_snoozed_ids || []).length,
      renderCell: (params) => {
        const snoozedIds = params.row.controls_snoozed_ids || [];
        if (snoozedIds.length === 0)
          return (
            <Typography variant="body2" sx={{ fontSize: "13px" }}>
              0
            </Typography>
          );
        return (
          <CustomTooltip title={snoozedIds.join(", ")}>
            <Typography
              variant="body2"
              sx={{
                cursor: "pointer",
                color: colors.warning,
                fontSize: "13px",
              }}
            >
              {snoozedIds.length}
            </Typography>
          </CustomTooltip>
        );
      },
    },
    {
      field: "controls_excluded_ids",
      headerName: "Excluded",
      flex: 0.6,
      align: "left",
      headerAlign: "left",
      valueGetter: (params) => (params.row?.controls_excluded_ids || []).length,
      renderCell: (params) => {
        const excludedIds = params.row.controls_excluded_ids || [];
        if (excludedIds.length === 0)
          return (
            <Typography variant="body2" sx={{ fontSize: "13px" }}>
              0
            </Typography>
          );
        return (
          <CustomTooltip title={excludedIds.join(", ")}>
            <Typography
              variant="body2"
              sx={{
                cursor: "pointer",
                color: colors.text_secondary,
                fontSize: "13px",
              }}
            >
              {excludedIds.length}
            </Typography>
          </CustomTooltip>
        );
      },
    },
    {
      field: "entities_total",
      headerName: "Total Entities",
      flex: 0.5,
      align: "left",
      headerAlign: "left",
      renderCell: (params) => (
        <Typography variant="body2" sx={{ fontSize: "13px" }}>
          {params.value}
        </Typography>
      ),
    },
    {
      field: "updated_at",
      headerName: "Updated",
      flex: 1,
      renderCell: (params) => {
        const date = new Date(params.value);
        return (
          <Typography variant="body2" sx={{ fontSize: "13px" }}>
            {date.toLocaleString()}
          </Typography>
        );
      },
    },
  ];

  return (
    <Box
      sx={{ height: "100%", display: "flex", flexDirection: "column", gap: 2 }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          gap: 1.5,
          alignItems: "center",
          padding: "8px 0",
          borderBottom: `1px solid ${colors.divider2}`,
        }}
      >
        <Box sx={{ minWidth: "160px" }}>
          <FilterMenu
            filterName="Env"
            fieldName="envFilter"
            options={envOptions}
            appliedFilters={appliedFilters}
            setAppliedFilters={setAppliedFilters}
            showSearch={true}
          />
        </Box>
        <Box sx={{ minWidth: "160px" }}>
          <FilterMenu
            filterName="Customer"
            fieldName="customerFilter"
            options={customerOptions}
            appliedFilters={appliedFilters}
            setAppliedFilters={setAppliedFilters}
            showSearch={true}
          />
        </Box>
        <Box sx={{ minWidth: "180px" }}>
          <SearchAndOrFilter
            filterName="Tool"
            fieldName="toolFilter"
            options={toolOptions}
            appliedFilters={appliedFilters}
            setAppliedFilters={setAppliedFilters}
            setFilterLogic={(logic) =>
              setFilterAndOrLogics((prev) => ({ ...prev, toolFilter: logic }))
            }
            filterLogic={filterAndOrLogics.toolFilter}
          />
        </Box>
        <Box sx={{ flex: 1 }}>
          <SearchInput
            fieldName="searchString"
            appliedFilters={appliedFilters}
            setAppliedFilters={setAppliedFilters}
            placeholder="Search customers, envs..."
          />
        </Box>
        <Box>
          <TextButton onClick={handleClearFilters} size="small">
            Clear
          </TextButton>
        </Box>
      </Box>

      <Box sx={{ flex: 1, minHeight: 0 }}>
        <StyledDataGrid
          rows={filteredCustomers.map((customer, idx) => ({
            id: idx,
            ...customer,
          }))}
          columns={customerColumns}
          loading={isLoading}
          disableRowSelectionOnClick
          pageSizeOptionsOverride={[10, 25, 50]}
          autoHeight={false}
          boxSXProps={{ height: "100%" }}
        />
      </Box>
    </Box>
  );
};

export default CustomersTableWidget;
